// HotelModal: Create/Edit hotel and rooms, link to packages
import React, { useState, useEffect } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Box, Typography, IconButton, Chip, CircularProgress } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import api from '../services/api';

function HotelModal({ open, onClose, onSave, editHotel }) {
  const [form, setForm] = useState({
    name: '', address: '', phone: '', email: '', website: '', description: '', rooms: [], primaryImage: '', secondaryImage: '', rating: '', images: []
  });
  const [room, setRoom] = useState({ type: '', price: '', capacity: '', includes: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  useEffect(() => {
    if (editHotel) setForm({ ...editHotel, rooms: editHotel.rooms || [], primaryImage: editHotel.primaryImage || '', secondaryImage: (editHotel.images && editHotel.images[0]) || '', images: editHotel.images || [], rating: editHotel.rating || '' });
    else setForm({ name: '', address: '', phone: '', email: '', website: '', description: '', rooms: [], primaryImage: '', secondaryImage: '', rating: '', images: [] });
  }, [editHotel, open]);

  const handleChange = e => {
    const { name, value } = e.target;
    setForm(f => ({ ...f, [name]: value }));
  };
  const handleRoomChange = e => {
    const { name, value } = e.target;
    setRoom(r => ({ ...r, [name]: value }));
  };
  const addRoom = () => {
    if (!room.type || !room.price) return;
    setForm(f => ({ ...f, rooms: [...(f.rooms || []), { ...room, price: Number(room.price), capacity: Number(room.capacity) }] }));
    setRoom({ type: '', price: '', capacity: '', includes: '' });
  };
  const removeRoom = idx => setForm(f => ({ ...f, rooms: f.rooms.filter((_, i) => i !== idx) }));

  const handleSubmit = async () => {
    setLoading(true);
    setError('');
    try {
      // Only send primaryImage and at most one secondary image to keep display simple
      const payload = { ...form };
      payload.images = form.secondaryImage ? [form.secondaryImage] : [];
      // ensure numeric fields
      if (payload.rating) payload.rating = Number(payload.rating);
      payload.rooms = (payload.rooms || []).map(r => ({ ...r, price: Number(r.price || 0), capacity: Number(r.capacity || 0) }));
      if (editHotel) await api.put(`/api/hotels/${editHotel._id}`, payload);
      else await api.post('/api/hotels', payload);
      onSave();
    } catch (err) {
      setError(err.response?.data?.error || 'Save failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{editHotel ? 'Edit Hotel' : 'Add Hotel / Accommodation'}</DialogTitle>
      <DialogContent>
        <TextField label="Name" name="name" value={form.name} onChange={handleChange} fullWidth sx={{ mb: 2 }} />
        <TextField label="Address" name="address" value={form.address} onChange={handleChange} fullWidth sx={{ mb: 2 }} />
        <TextField label="Phone" name="phone" value={form.phone} onChange={handleChange} fullWidth sx={{ mb: 2 }} />
        <TextField label="Email" name="email" value={form.email} onChange={handleChange} fullWidth sx={{ mb: 2 }} />
        <TextField label="Website" name="website" value={form.website} onChange={handleChange} fullWidth sx={{ mb: 2 }} />
        <TextField label="Description" name="description" value={form.description} onChange={handleChange} fullWidth multiline rows={2} sx={{ mb: 2 }} />

        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2">Primary Image</Typography>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 1 }}>
            <input accept="image/*" style={{ display: 'none' }} id="hotel-primary-upload" type="file" onChange={async e => {
              const file = e.target.files?.[0]; if (!file) return; setUploading(true); setUploadProgress(0);
              try {
                const fd = new FormData(); fd.append('file', file);
                const res = await api.post('/api/storage/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' }, onUploadProgress: p => setUploadProgress(Math.round((p.loaded / p.total) * 100)) });
                const url = res.data?.url;
                if (url) setForm(f=>({...f, primaryImage: url}));
                else { const reader = new FileReader(); reader.onload = ev => setForm(f=>({...f, primaryImage: ev.target.result})); reader.readAsDataURL(file); }
              } catch (err) {
                const reader = new FileReader(); reader.onload = ev => setForm(f=>({...f, primaryImage: ev.target.result})); reader.readAsDataURL(file);
              } finally { setUploading(false); setUploadProgress(0); }
            }} />
            <label htmlFor="hotel-primary-upload"><Button component="span" variant="contained" size="small" disabled={uploading}>{uploading ? `Uploading ${uploadProgress}%` : 'Upload Primary'}</Button></label>
            <TextField placeholder="https://... or paste storage link" size="small" label="Primary Image URL" value={form.primaryImage} onChange={e=>setForm(f=>({...f, primaryImage: e.target.value}))} fullWidth sx={{ ml: 1 }} />
          </Box>
          {form.primaryImage && <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}><img src={form.primaryImage} alt="primary" style={{ width: 160, borderRadius: 6 }} /><IconButton size="small" onClick={() => setForm(f=>({...f, primaryImage: ''}))}><CloseIcon fontSize="small" /></IconButton></Box>}
        </Box>

        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2">Secondary Image (optional)</Typography>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 1 }}>
            <input accept="image/*" style={{ display: 'none' }} id="hotel-secondary-upload" type="file" onChange={async e => {
              const file = e.target.files?.[0]; if (!file) return; setUploading(true); setUploadProgress(0);
              try {
                const fd = new FormData(); fd.append('file', file);
                const res = await api.post('/api/storage/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' }, onUploadProgress: p => setUploadProgress(Math.round((p.loaded / p.total) * 100)) });
                const url = res.data?.url;
                if (url) setForm(f=>({...f, secondaryImage: url}));
                else { const reader = new FileReader(); reader.onload = ev => setForm(f=>({...f, secondaryImage: ev.target.result})); reader.readAsDataURL(file); }
              } catch (err) {
                const reader = new FileReader(); reader.onload = ev => setForm(f=>({...f, secondaryImage: ev.target.result})); reader.readAsDataURL(file);
              } finally { setUploading(false); setUploadProgress(0); }
            }} />
            <label htmlFor="hotel-secondary-upload"><Button component="span" variant="contained" size="small" disabled={uploading}>{uploading ? `Uploading ${uploadProgress}%` : 'Upload Secondary'}</Button></label>
            <TextField placeholder="https://... or paste storage link" size="small" label="Secondary Image URL" value={form.secondaryImage} onChange={e=>setForm(f=>({...f, secondaryImage: e.target.value}))} fullWidth sx={{ ml: 1 }} />
          </Box>
          {form.secondaryImage && <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}><img src={form.secondaryImage} alt="secondary" style={{ width: 120, borderRadius: 6 }} /><IconButton size="small" onClick={() => setForm(f=>({...f, secondaryImage: ''}))}><CloseIcon fontSize="small" /></IconButton></Box>}
        </Box>

        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2">Rooms</Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr auto' }, gap: 1, mb: 1, alignItems: 'center' }}>
            <TextField label="Type" name="type" value={room.type} onChange={handleRoomChange} size="small" fullWidth sx={{ gridColumn: '1 / span 1' }} placeholder="e.g., Deluxe" />
            <TextField label="Price" name="price" value={room.price} onChange={handleRoomChange} size="small" type="number" sx={{ gridColumn: '2 / span 1' }} placeholder="0" />
            <TextField label="Capacity" name="capacity" value={room.capacity} onChange={handleRoomChange} size="small" type="number" sx={{ gridColumn: '3 / span 1' }} placeholder="2" />
            <TextField label="Includes (comma separated)" name="includes" value={room.includes} onChange={handleRoomChange} size="small" sx={{ gridColumn: '1 / span 3' }} placeholder="Breakfast, Wifi" />
            <Button onClick={addRoom} variant="contained" size="small" sx={{ gridColumn: '4 / span 1', justifySelf: 'end' }}>Add</Button>
          </Box>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {(form.rooms || []).map((r, idx) => (
              <Chip key={idx} label={`${r.type} • ${r.price} • ${r.capacity} • ${(r.includes || '').substring(0,40)}`} onDelete={() => removeRoom(idx)} />
            ))}
          </Box>
        </Box>
        {error && <Typography color="error" mb={1}>{error}</Typography>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSubmit} variant="contained" disabled={loading}>{loading ? <CircularProgress size={22} /> : 'Save'}</Button>
      </DialogActions>
    </Dialog>
  );
}

export default HotelModal;
